import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Select.css';

/**
 * Select Component
 * Shared select/dropdown component for consistent styling
 *
 * Features:
 * - Supports array of options or value/label objects
 * - Error states
 * - Disabled states
 * - Helper text
 * - Full accessibility support
 */
const Select = forwardRef(
  (
    {
      value,
      onChange,
      options = [],
      placeholder,
      disabled = false,
      required = false,
      error,
      helperText,
      className = '',
      ariaLabel,
      id,
      name,
      ...rest
    },
    ref
  ) => {
    const selectClasses = [
      'shared-select',
      error && 'select-error',
      disabled && 'select-disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleChange = onChange
      ? (e) => {
          const newValue = e.target.value;
          // Try to parse as number if it looks like a number
          const parsedValue = isNaN(newValue) ? newValue : Number(newValue);
          onChange(parsedValue);
        }
      : undefined;

    // Normalize options to always have value/label structure
    const normalizedOptions = options.map((opt) => {
      if (typeof opt === 'object' && opt !== null) {
        return opt;
      }
      return { value: opt, label: opt };
    });

    // Support both controlled and uncontrolled modes
    const selectProps = {
      ref,
      id,
      name,
      className: selectClasses,
      disabled,
      required,
      'aria-label': ariaLabel,
      'aria-invalid': !!error,
      'aria-describedby': error || helperText ? `${id}-description` : undefined,
      ...rest,
    };

    // Only add value/onChange for controlled selects
    if (value !== undefined) {
      selectProps.value = value;
    }
    if (handleChange !== undefined) {
      selectProps.onChange = handleChange;
    }

    return (
      <div className="select-wrapper">
        <select {...selectProps}>
          {placeholder && (
            <option value="" disabled={required}>
              {placeholder}
            </option>
          )}
          {normalizedOptions.map((option, index) => (
            <option key={option.value ?? index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {(helperText || error) && (
          <div
            id={`${id}-description`}
            className={`select-description ${error ? 'select-error-message' : 'select-helper-text'}`}
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

Select.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.node.isRequired,
      }),
    ])
  ).isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Select;
